<template>
    <div class="data-page data-page-centered">
        <PageHeader title="Transação Manual"
            subtitle="Gera um depósito ou realiza um saque manual a partir do saldo de um fornecedor" />

        <div class="data-card data-form">
            <div v-if="stateVendors.loading.vendors" class="data-form-loading">
                <ProgressSpinner />
            </div>

            <form v-else class="data-form-grid" @submit.prevent="openConfirmation">

                <div class="form-field">
                    <label for="transaction-type">Tipo da transação</label>
                    <Dropdown inputId="transaction-type" v-model="state.type" :options="state.types"
                        optionLabel="name" placeholder="Selecione o tipo" />
                </div>

                <div class="form-field">
                    <label for="transaction-vendor">Fornecedor</label>
                    <Dropdown inputId="transaction-vendor" v-model="state.vendor" :options="activeVendors"
                        optionLabel="name" placeholder="Selecione o fornecedor" />

                    <!-- só informativo: falhar aqui nunca impede a transação -->
                    <template v-if="state.vendor">
                        <small v-if="state.loading.balances" class="data-detail-hint">
                            Consultando o saldo registrado...
                        </small>
                        <template v-else-if="selectedVendorBalance">
                            <small class="data-detail-hint">
                                Saldo registrado no hub:
                                <strong class="cell-amount" :class="{ 'is-negative': selectedVendorBalance.balance < 0 }">
                                    {{ formatCurrency(selectedVendorBalance.balance) }}
                                </strong>
                                <template v-if="selectedVendorBalance.last_entry_at">
                                    · última movimentação em {{ formatDate(selectedVendorBalance.last_entry_at) }}
                                </template>
                            </small>
                            <small class="form-hint">
                                O saldo do hub é uma referência e pode estar diferente do real. Antes de
                                operar, confira e siga o saldo do painel do fornecedor.
                            </small>
                        </template>
                        <small v-else class="data-detail-hint">
                            Saldo registrado indisponível no momento. Confira o saldo no painel do fornecedor.
                        </small>
                    </template>
                </div>

                <div class="form-field">
                    <label for="transaction-value">Valor</label>
                    <InputNumber inputId="transaction-value" v-model="state.value" mode="currency" currency="BRL"
                        locale="pt-BR" :min="0" :max="999999.99" :maxFractionDigits="2" />
                </div>

                <!-- o Metagrator ignora a expiração do depósito -->
                <div v-if="!isWithdraw && !isMetagrator" class="form-field">
                    <label>Expiração do depósito (opcional)</label>
                    <CalendarForm v-model="state.expirationDate" showTime showIcon placeholder="Sem expiração" />
                </div>

                <div class="data-form-section-title" :class="isWithdraw ? 'is-withdraw' : 'is-deposit'">
                    <i :class="isWithdraw ? 'pi pi-arrow-circle-up' : 'pi pi-arrow-circle-down'"></i>
                    {{ isWithdraw ? 'Dados do recebedor' : 'Dados do jogador' }}
                </div>

                <div class="form-field">
                    <label for="transaction-player-name">Nome</label>
                    <InputText id="transaction-player-name" type="text" v-model="state.player_name" />
                </div>

                <div class="form-field">
                    <label for="transaction-player-document">Documento (CPF/CNPJ)</label>
                    <InputText id="transaction-player-document" type="text" v-model="state.cpf_cnpj"
                        :class="{ 'p-invalid': documentError }" aria-describedby="transaction-player-document-hint" />
                    <small v-if="documentError" id="transaction-player-document-hint" class="form-hint">
                        {{ documentError }}
                    </small>
                </div>

                <div class="form-field">
                    <label for="transaction-player-email">E-mail</label>
                    <InputText id="transaction-player-email" type="text" v-model="state.player_email"
                        :class="{ 'p-invalid': emailError }" aria-describedby="transaction-player-email-hint" />
                    <small v-if="emailError" id="transaction-player-email-hint" class="form-hint">
                        {{ emailError }}
                    </small>
                </div>

                <template v-if="isWithdraw">

                    <div class="data-form-section-title">
                        <i class="pi pi-key"></i>
                        Chave PIX
                    </div>

                    <div class="form-field">
                        <label for="transaction-key-type">Tipo da chave PIX</label>
                        <Dropdown inputId="transaction-key-type" v-model="state.pixKeyType" optionLabel="name"
                            :options="pixKeyOptions" placeholder="Selecione o tipo" />
                    </div>

                    <div class="form-field">
                        <label for="transaction-key">Chave PIX{{ state.pixKeyType ? ` (${state.pixKeyType.name})` : ''
                            }}</label>
                        <InputText id="transaction-key" type="text" v-model="state.pixKey"
                            :class="{ 'p-invalid': pixKeyError }" aria-describedby="transaction-key-hint" />
                        <small v-if="pixKeyError" id="transaction-key-hint" class="form-hint">
                            {{ pixKeyError }}
                        </small>
                        <small v-if="isLionpay" class="form-hint">
                            Sem +55 e sem máscara. CPF e celular vão só com os números.
                        </small>
                    </div>

                </template>

                <div class="data-form-footer">
                    <Button type="submit" :loading="state.loading.tryingToMakeTransaction" :label="submitLabel"
                        :disabled="!canSubmit" icon="pi pi-check" iconPos="right" />
                </div>

                <div v-if="state.result" class="data-detail-section data-form-full">
                    <span class="data-detail-section-title">Transação #{{ state.result.id }} criada</span>

                    <div v-if="state.result.qr_code_image" class="data-detail-qr">
                        <img :src="formatQRCode(state.result.qr_code_image)" width="200" height="200"
                            alt="QR Code do depósito" />
                    </div>

                    <div v-if="state.result.qr_code" class="form-field">
                        <label for="transaction-qr-code">Copia e cola</label>
                        <Textarea id="transaction-qr-code" v-model="state.result.qr_code" rows="3" readonly />
                        <div class="data-detail-actions">
                            <Button type="button" class="p-button-outlined" label="Copiar" icon="pi pi-copy"
                                @click="copyToClipboard(state.result.qr_code)" />
                        </div>
                    </div>
                </div>

            </form>
        </div>

        <Dialog class="data-dialog" v-model:visible="state.confirmation.visible" :style="{ width: '34rem' }" modal
            :header="isWithdraw ? 'Confirmar saque' : 'Confirmar depósito'"
            :closable="!state.loading.tryingToMakeTransaction"
            :closeOnEscape="!state.loading.tryingToMakeTransaction">

            <div class="data-detail-body">
                <div class="data-detail-grid">
                    <div class="data-detail-item">
                        <span class="data-detail-label">Fornecedor</span>
                        <span class="data-detail-value">{{ state.vendor?.name }}</span>
                    </div>
                    <div class="data-detail-item">
                        <span class="data-detail-label">Tipo</span>
                        <span class="data-detail-value">{{ state.type?.name }}</span>
                    </div>
                    <div class="data-detail-item">
                        <span class="data-detail-label">Valor</span>
                        <span class="data-detail-value cell-amount">{{ formatCurrency(state.value) }}</span>
                    </div>
                    <div class="data-detail-item">
                        <span class="data-detail-label">{{ isWithdraw ? 'Recebedor' : 'Jogador' }}</span>
                        <span class="data-detail-value">{{ state.player_name }}</span>
                    </div>
                    <div class="data-detail-item">
                        <span class="data-detail-label">Documento</span>
                        <span class="data-detail-value">{{ state.cpf_cnpj }}</span>
                    </div>
                    <div v-if="isWithdraw" class="data-detail-item">
                        <span class="data-detail-label">Chave PIX</span>
                        <span class="data-detail-value">{{ state.pixKeyType?.name }} · {{ state.pixKey }}</span>
                    </div>
                    <div v-else-if="state.expirationDate && !isMetagrator" class="data-detail-item">
                        <span class="data-detail-label">Expiração</span>
                        <span class="data-detail-value">{{ formatLocalDate(state.expirationDate) }}</span>
                    </div>
                </div>

                <small class="data-detail-hint">
                    {{ isWithdraw
                        ? 'Confira os dados: o valor sai da conta do fornecedor e não pode ser desfeito pelo hub.'
                        : 'Será gerado um QR code PIX para pagamento.' }}
                </small>
            </div>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar"
                    :disabled="state.loading.tryingToMakeTransaction" @click="state.confirmation.visible = false" />
                <Button type="button" :label="isWithdraw ? 'Confirmar saque' : 'Confirmar depósito'" icon="pi pi-check"
                    :loading="state.loading.tryingToMakeTransaction" @click="tryToMakeTransaction" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Dropdown from 'primevue/dropdown'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import CalendarForm from '../../components/CalendarForm.vue'
import PageHeader from '../../components/PageHeader.vue'
import services from '../../services'
import { computed, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import useNotificationToast from '../../composables/useNotificationToast'
import useVendors from '../../composables/useVendors'
import ProgressSpinner from 'primevue/progressspinner'
import moment from 'moment'
import { isValidDocument, isValidEmail, isValidPixKey, onlyDigits } from '../../utils/document'

export default {

    components: {
        InputText,
        InputNumber,
        Dropdown,
        Textarea,
        Button,
        Dialog,
        CalendarForm,
        PageHeader,
        ProgressSpinner
    },

    setup() {

        const generateOptions = (names, values) => {
            const options = []

            for (let i = 0; i < names.length; i++) {
                options.push({ name: names[i], value: values[i] })
            }

            return options
        }

        const state = reactive({
            type: { name: 'Saque', value: 'WITHDRAW' },
            types: [
                { name: 'Depósito', value: 'DEPOSIT' },
                { name: 'Saque', value: 'WITHDRAW' }
            ],
            vendor: null,
            pixKeyType: null,
            pixKey: null,
            value: null,
            player_name: null,
            player_email: null,
            cpf_cnpj: null,
            expirationDate: null,
            result: null,
            confirmation: { visible: false },
            loading: {
                tryingToMakeTransaction: false,
                vendors: false,
                balances: false
            },
            // saldo registrado por fornecedor (tela de Saldo de Fornecedores); só informativo
            vendorBalances: [],
            optionsNames: ['CNPJ', 'CPF', 'CELULAR', 'EMAIL', 'CHAVE ALEATÓRIA'],
            optionsValues: ['CNPJ', 'CPF', 'PHONE', 'EMAIL'],
            vendorsPixKeys: null
        })

        state.vendorsPixKeys = {
            SHIPAY: generateOptions(state.optionsNames, [...state.optionsValues, 'RANDOM_KEY']),
            PRIMEPAG: generateOptions(state.optionsNames, [...state.optionsValues, 'TOKEN']),
            PAYBROKERS: generateOptions(state.optionsNames, [...state.optionsValues, 'EVP']),
            // o payout do Lionpay leva a chave em walletId e o tipo em cardType, então aceita qualquer
            // chave — só CNPJ não existe do lado dele
            LIONPAY: generateOptions(state.optionsNames.slice(1), [...state.optionsValues.slice(1), 'EVP'])
        }

        // Fornecedores sem chave específica usam a lista padrão
        const defaultPixKeys = generateOptions(state.optionsNames, [...state.optionsValues, 'RANDOM_KEY'])

        const store = useStore()
        const { success, error } = useNotificationToast()
        const { stateVendors, loadVendorsWithoutPagination } = useVendors()

        // todos os fornecedores, sem paginação; só os ativos podem receber transação nova
        loadVendorsWithoutPagination()

        // `active` chega como 0/1 da listagem
        const activeVendors = computed(() => (stateVendors.vendors || []).filter((vendor) => !!vendor.active))

        const isWithdraw = computed(() => state.type && state.type.value === 'WITHDRAW')

        const submitLabel = computed(() => isWithdraw.value ? 'Realizar Saque' : 'Realizar Depósito')

        const isLionpay = computed(() => state.vendor && state.vendor.type === 'LIONPAY')

        const isMetagrator = computed(() => state.vendor && state.vendor.type === 'METAGRATOR')

        const documentError = computed(() => state.cpf_cnpj && !isValidDocument(state.cpf_cnpj)
            ? 'CPF/CNPJ inválido.'
            : null)

        const emailError = computed(() => state.player_email && !isValidEmail(state.player_email)
            ? 'E-mail inválido.'
            : null)

        const pixKeyError = computed(() => {
            if (!isWithdraw.value || !state.pixKey || !state.pixKeyType) return null

            if (!isValidPixKey(state.pixKeyType.value, state.pixKey)) {
                return `Chave inválida para o tipo ${state.pixKeyType.name}.`
            }

            // com o módulo de saques externos desligado, o Metagrator só paga ao titular do cadastro
            const isDocumentKey = ['CPF', 'CNPJ'].includes(state.pixKeyType.value)
            if (isMetagrator.value && isDocumentKey && onlyDigits(state.pixKey) !== onlyDigits(state.cpf_cnpj)) {
                return 'No Metagrator, a chave CPF/CNPJ precisa ser o documento do recebedor.'
            }

            return null
        })

        const canSubmit = computed(() => {
            const missingPixKey = isWithdraw.value && (!state.pixKey || !state.pixKeyType)

            return Boolean(
                state.vendor &&
                state.value > 0 &&
                state.player_name?.trim() &&
                state.cpf_cnpj &&
                state.player_email &&
                !missingPixKey &&
                !documentError.value &&
                !emailError.value &&
                !pixKeyError.value
            )
        })

        const selectedVendorBalance = computed(() => {
            if (!state.vendor) return null

            return state.vendorBalances.find((item) => item.vendor_id === state.vendor.id) || null
        })

        // falha aqui não avisa com toast nem bloqueia nada: a tela mostra "indisponível" e a
        // transação segue normalmente
        function loadVendorBalances() {
            state.loading.balances = true

            services.vendorBalances.getAll({ token: store.getters.getToken })
                .then((response) => { state.vendorBalances = response.data })
                .catch(() => { state.vendorBalances = [] })
                .finally(() => { state.loading.balances = false })
        }

        loadVendorBalances()

        const pixKeyOptions = computed(() => {
            if (!state.vendor) return defaultPixKeys

            return state.vendorsPixKeys[state.vendor.type] || defaultPixKeys
        })

        watch(pixKeyOptions, (newValue) => {

            state.pixKeyType = newValue[0]

        }, { immediate: true })

        // o resultado fica no mesmo card do formulário: trocar o tipo não pode
        // deixar o QR code do depósito anterior sobrando na tela do saque
        watch(() => state.type, () => {

            state.result = null

        })

        function clearForm() {
            state.pixKey = null
            state.value = null
            state.player_name = null
            state.player_email = null
            state.cpf_cnpj = null
            state.expirationDate = null
        }

        function formatLocalDate(value) {
            return moment(value).format('DD/MM/YYYY HH:mm')
        }

        function openConfirmation() {
            if (!canSubmit.value) return error('Verifique todos os campos!')

            state.confirmation.visible = true
        }

        function formatCurrency(value) {
            return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }

        // as datas chegam no horário de Brasília com sufixo Z, como no resto do painel
        function formatDate(value) {
            const date = moment.utc(value)

            return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '—'
        }

        function formatQRCode(value) {
            return value.includes('data:image/png;base64,') ? value : 'data:image/png;base64,' + value
        }

        function copyToClipboard(value) {
            navigator.clipboard.writeText(value)
                .then(() => success('Copia e cola copiado!'))
                .catch(() => error('Não foi possível copiar.'))
        }

        async function tryToMakeTransaction() {

            if (!canSubmit.value) {
                state.confirmation.visible = false
                return error('Verifique todos os campos!')
            }

            state.loading.tryingToMakeTransaction = true

            const token = store.getters.getToken

            await services.transactions.createManualTransaction({
                type: state.type.value,
                vendor: state.vendor,
                pixKeyType: isWithdraw.value ? state.pixKeyType.value : undefined,
                pixKey: isWithdraw.value ? state.pixKey.trim() : undefined,
                expirationDate: isWithdraw.value || isMetagrator.value ? undefined : state.expirationDate,
                value: state.value,
                // sem código: o recebedor do painel tem o código gerado pelo hub
                player: {
                    cpf: onlyDigits(state.cpf_cnpj),
                    name: state.player_name.trim(),
                    email: state.player_email.trim()
                },
                token
            }).then((response) => {

                state.result = isWithdraw.value ? null : response.data

                success(isWithdraw.value
                    ? 'Saque realizado com sucesso!'
                    : 'Depósito gerado com sucesso!')

                clearForm()
                loadVendorBalances()

            })
                .catch((e) => {
                    const message = e.response?.data?.message
                    if (!message) return error('Não foi possível concluir a transação.')
                    error(message.charAt(0).toUpperCase() + message.slice(1))
                })
                .finally(() => {
                    state.loading.tryingToMakeTransaction = false
                    state.confirmation.visible = false
                })

        }


        return {
            state,
            stateVendors,
            isWithdraw,
            submitLabel,
            isLionpay,
            isMetagrator,
            activeVendors,
            documentError,
            emailError,
            pixKeyError,
            canSubmit,
            openConfirmation,
            formatLocalDate,
            selectedVendorBalance,
            formatCurrency,
            formatDate,
            pixKeyOptions,
            formatQRCode,
            copyToClipboard,
            tryToMakeTransaction
        }
    }
}

</script>

